import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import intake from "../assets/intake.jpg";
import security from "../assets/security.jpg";
import ehr from "../assets/ehr.jpg";
import integration from "../assets/integration.jpg";

const Solutions = () => {
  const cardsData = [
    {
      id: 1,
      title: "AI Intake & Triage",
      description:
        "Conversational AI engages patients before the visit, gathering key symptoms and concerns using natural language.",
      image: intake,
    },
    {
      id: 2,
      title: "Automated Documentation",
      description:
        "Automatically generates SOAP notes, encounter summaries, and EHR-ready inputs based on the conversation.",
      image: ehr,
    },
    {
      id: 3,
      title: "Seamless Integration",
      description:
        "Cura AI connects with your existing systems – from scheduling to charity – to reduce duplication and increase efficiency.",
      image: integration,
    },
    {
      id: 4,
      title: "Privacy & Security",
      description:
        "PHIPA & HIPAA-compliant architecture with advanced consent management and audit trails.",
      image: security,
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(1115);
  const [isMobile, setIsMobile] = useState(false);
  const gap = 24;

  useEffect(() => {
    const updateDimensions = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);

      if (window.innerWidth < 1200) {
        setCardWidth(window.innerWidth * 0.9);
      } else {
        setCardWidth(1115);
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const totalCardWidth = cardWidth + gap;
  const offset = totalCardWidth * currentIndex;

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? cardsData.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === cardsData.length - 1 ? 0 : prev + 1));
  };

  return (
    <motion.div
      className="relative w-screen flex flex-col items-center bg-[#427dfc1c] px-4 pt-35 pb-16 text-white"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, amount: 0.2 }}
    >
      <div className="max-w-[1000px] mt-[-96px] w-full mx-auto text-center flex flex-col justify-center">
        <motion.h1
          className="text-black md:text-4xl sm:text-3xl text-3xl font-medium pt-10 mb-2"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          How Cura AI Works
        </motion.h1>
        <motion.p
          className="text-gray-600 md:text-lg text-lg pt-1 mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          Beyond Agents: A Complete Digital Infrastructure
        </motion.p>
      </div>

      <div className="overflow-hidden w-screen">
        <div
          className="flex transition-transform duration-500 ease-in-out gap-6"
          style={{
            transform: `translateX(calc(-${offset}px + 50vw - ${cardWidth / 2}px))`,
          }}
        >
          {cardsData.map((card, index) => {
            const isActive = index === currentIndex;

            return (
              <div
                key={card.id}
                className="relative flex-shrink-0 rounded-xl overflow-hidden shadow-xl transition-opacity duration-500"
                style={{
                  width: cardWidth,
                  height: 550,
                  opacity: isMobile ? (isActive ? 1 : 0.4) : 1,
                }}
              >
                <img
                  src={card.image}
                  alt={card.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />

                <motion.div
                  className="absolute inset-0 bg-black/30 flex flex-col justify-center items-center text-center text-white px-6"
                  initial={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
                  whileHover={
                    !isMobile && isActive
                      ? { backgroundColor: "rgba(0, 0, 0, 0.5)" }
                      : {}
                  }
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  <motion.h2
                    className="text-4xl font-medium mb-2"
                    initial={{ scale: 1, textShadow: "0px 0px 0px rgba(0,0,0,0)" }}
                    whileHover={
                      !isMobile && isActive
                        ? {
                            scale: 1.05,
                            textShadow: "0px 0px 10px rgba(0,0,0,0.5)",
                          }
                        : {}
                    }
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  >
                    {card.title}
                  </motion.h2>
                  <motion.p
                    className="text-lg"
                    initial={{ scale: 1 }}
                    whileHover={
                      !isMobile && isActive ? { scale: 1.05 } : {}
                    }
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  >
                    {card.description}
                  </motion.p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-center mt-12 space-x-4">
        <button
          onClick={prevSlide}
          className="bg-[#427dfc00] text-[#427cfc] p-2 cursor-pointer transition-colors"
        >
          <GoChevronLeft className="text-[#427cfc]" size={35} />
        </button>

        <div className="flex space-x-6">
          {cardsData.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-3 w-3 rounded-3xl transition-colors duration-300 ${
                currentIndex === index ? "bg-[#427cfc]" : "bg-gray-300"
              }`}
            />
          ))}
        </div>


        <button
          onClick={nextSlide}
          className="bg-[#427dfc00] text-black p-2 cursor-pointer transition-colors"
        >
          <GoChevronRight className="text-[#427cfc]" size={35} />
        </button>
      </div>
    </motion.div>
  );
};

export default Solutions;